'use client';

import type { DragEndEvent } from '@dnd-kit/core';
import type { JSX } from 'react';
import {
  closestCenter,
  DndContext,

  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useState } from 'react';
import { SortableItem } from './SortableItem';

export function SortableList({
  items,
  onSortEnd,
  disabled,
  inputRef,
}: {
  items: JSX.Element[];
  onSortEnd: (oldIndex: number, newIndex: number) => void;
  disabled: boolean;
  inputRef: React.MutableRefObject<any>;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const itemIds = items.map((_, index) => `item-${index}`);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragEndEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = itemIds.indexOf(active.id as string);
      const newIndex = itemIds.indexOf(over.id as string);

      if (oldIndex !== newIndex) {
        onSortEnd(oldIndex, newIndex);
      }
    }

    setActiveId(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={disabled ? [] : itemIds} strategy={verticalListSortingStrategy}>
        <ul>
          {items?.length > 0
            && items.map((value, index) => (
              <SortableItem
                key={`item-${index}`}
                id={`item-${index}`}
                value={value}
                position={index}
                inputRef={inputRef}
                isDragging={`item-${index}` === activeId}
              />
            ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
